import { inject, Injectable } from '@angular/core';
import { AppDataConfig } from '@core/configs/app-data.config';
import { FeaturesHttpEndpoints } from '@core/enums/features-http-endpoints';
import {
  AppDataContainers,
  AppDataFieldsRequest,
} from '@core/models/interfaces/app-data/app-data';
import { AppDataField } from '@core/models/interfaces/app-data/app-data-field';
import { AppDataResponse } from '@core/models/interfaces/http/app-data-response';
import { DataProviderService } from '@core/services/data-provider/data-provider.service';
import { StorageService } from '@core/services/storages/storage.service';
import { forkJoin, map, Observable, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppDataProviderService extends DataProviderService {
  protected storage = inject(StorageService);

  /**
   * Fetches application data based on provided fields or fetches all data if no fields specified.
   * @param fields Optional specific fields to fetch.
   * @param resetAnchor
   * @returns Observable containing fetched application data.
   */
  public getAppData(
    // R75, the other half. This said the values were *container values*, while every caller passes
    // paging terms — and it compiled only because `AppDataContainers` resolves to `AppDataField |
    // any`, so the `any` absorbed the disagreement. `AppDataFieldsRequest` names what is really
    // sent, on both sides of the boundary.
    fields: AppDataFieldsRequest = {},
    resetAnchor = false
  ): Observable<AppDataResponse> {
    let payload: AppDataFieldsRequest;
    if (fields && Object.keys(fields).length > 0) {
      payload = this.createPartialPayload(fields);
    } else {
      payload = this.createFullPayload();
    }

    return this.setRequestMetadata(payload, resetAnchor).pipe(
      switchMap((fields) =>
        this.create<Partial<AppDataContainers>, AppDataResponse>(
          FeaturesHttpEndpoints.FETCH_DATA,
          fields
        )
      )
    );
  }

  /**
   * Creates a payload object for partial data fetching based on provided fields.
   * @param fields Specific fields to include in payload.
   * @returns Payload object for partial fetching.
   */
  createPartialPayload(fields: AppDataFieldsRequest): AppDataFieldsRequest {
    const sanitizedFields = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [
        key,
        value ?? AppDataConfig.containers[key] ?? {},
      ])
    ) as AppDataFieldsRequest;

    return { ...sanitizedFields };
  }

  /**
   * Creates a payload object for fetching the full application data.
   * @returns Payload object containing all configured application data fields.
   */
  createFullPayload(): AppDataContainers {
    return { ...AppDataConfig.containers };
  }

  /**
   * Sets the `syncAnchor` property for each provided container, if applicable.
   *
   * For each container in the input `fields` object:
   * - If the container key is **not** listed in `AppDataConfig.batchedContainers`,
   *   the method overrides its value with an object containing only the `syncAnchor`
   *   property, which is taken from the corresponding local storage field's metadata.
   * - If the container **is** batched, the original value from `fields[key]` is used as is.
   *
   * This method constructs a new `Partial<AppDataContainers>` object with updated values
   * and returns it as an observable.
   *
   * @param fields A partial object of `AppDataContainers` where each key corresponds
   *        to a container to update. The value can be the current container state or `null`.
   * @param reset
   * @returns An `Observable` emitting a `Partial<AppDataContainers>` object with updated
   *          `syncAnchor` values (where applicable).
   */
  setRequestMetadata(
    fields: {
      [K in keyof AppDataContainers]?: AppDataContainers[K] | null;
    },
    reset: boolean
  ): Observable<Partial<AppDataContainers>> {
    const containers$ = (
      Object.keys(fields) as (keyof AppDataContainers)[]
    ).map((key) => {
      return this.storage.getField<AppDataField>(key).pipe(
        map((value: AppDataField | null) => {
          const metaData = value?.metaData;

          if (!AppDataConfig.batchedContainers.includes(key)) {
            return [
              key,
              { syncAnchor: !reset ? (metaData?.['syncAnchor'] ?? 0) : 0 },
            ] as const;
          }

          // R121. A batched container is anchored too, just by a different
          // field: `lastUpdatedDate`. The rewrite dropped this branch and sent
          // the caller's value through untouched — `{}` for a partial load, a
          // hard-coded zero during pagination — so every refresh asked for the
          // list from the top and re-downloaded all of it. The anchor the server
          // already returns and the client already stores was never sent back.
          const requested = (fields[key] ?? {}) as Record<string, unknown>;

          return [
            key,
            {
              ...requested,
              lastUpdatedDate: reset
                ? 0
                : (requested['lastUpdatedDate'] ??
                  metaData?.['lastUpdatedDate'] ??
                  0),
              limit:
                requested['limit'] ??
                metaData?.['limit'] ??
                AppDataConfig.containers[key]?.limit ??
                0,
              offset: requested['offset'] ?? 0,
            },
          ] as const;
        })
      );
    });

    return forkJoin(containers$).pipe(
      map((entries) => {
        return Object.fromEntries(entries) as Partial<AppDataContainers>;
      })
    );
  }
}
